import { useEffect, useState } from 'react';
import { metricsService } from '@services/api/metricsService';
import { MetricsCard } from '@components/dashboard/MetricsCard';
import { PriorityQueue } from '@components/dashboard/PriorityQueue';
import { ActivityFeed } from '@components/dashboard/ActivityFeed';
import { IntegrationStatus } from '@components/dashboard/IntegrationStatus';
import './Dashboard.css';

export const Dashboard = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [quickMetrics, setQuickMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    const [metricsResult, quickResult] = await Promise.all([
      metricsService.getDashboardMetrics(),
      metricsService.getQuickMetrics(),
    ]);

    if (metricsResult.success) {
      setMetrics(metricsResult.data);
    }

    if (quickResult.success) {
      setQuickMetrics(quickResult.data);
    }

    setLoading(false);
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle">Welcome to Mal Bank Operations</p>
        </div>
        <span className="dashboard-last-updated">
          Last updated: {loading ? 'Loading...' : 'Just now'}
        </span>
      </div>

      {/* AI Insights Panel - Placeholder for Phase 3 */}
      <div className="ai-insights-panel">
        <h3 className="ai-insights-title">🤖 AI Insights & Alerts</h3>
        <div className="ai-insights-content">
          <p className="text-secondary">
            AI-powered insights will appear here in Phase 3
          </p>
          <p className="text-small text-secondary">
            • Churn risk predictions • Unusual pattern detection • Proactive alerts
          </p>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <MetricsCard
          title="Active Customers"
          value={loading ? '...' : metrics?.active_customers.toLocaleString() || '0'}
          subtitle="Registered users"
          icon="👥"
          color="blue"
        />
        <MetricsCard
          title="Open Cases"
          value={loading ? '...' : metrics?.open_cases || '0'}
          subtitle="Avg: 4.2 days"
          icon="📋"
          color={metrics?.open_cases > 20 ? 'orange' : 'default'}
        />
        <MetricsCard
          title="Pending Transactions"
          value={loading ? '...' : metrics?.pending_transactions || '0'}
          subtitle={metrics?.pending_transactions > 5 ? '2 urgent' : 'All normal'}
          icon="💳"
          color={metrics?.pending_transactions > 5 ? 'red' : 'default'}
        />
        <MetricsCard
          title="System Health"
          value={loading ? '...' : '✅ All OK'}
          subtitle="4/4 systems online"
          color="green"
        />
      </div>

      {/* Priority Queue & Quick Metrics */}
      <div className="dashboard-row">
        <div className="dashboard-col-2">
          <PriorityQueue />
        </div>
        
        <div className="dashboard-col-1">
          <div className="quick-metrics-card">
            <h3 className="quick-metrics-title">📊 Quick Metrics</h3>
            
            {loading ? (
              <p className="text-secondary">Loading...</p>
            ) : (
              <>
                <div className="quick-metrics-section">
                  <h4 className="quick-metrics-section-title">Today:</h4>
                  <ul className="quick-metrics-list">
                    <li>• {quickMetrics?.today.logins || 0} logins</li>
                    <li>• {quickMetrics?.today.transactions || 0} transactions</li>
                    <li>• {quickMetrics?.today.new_customers || 0} new customers</li>
                  </ul>
                </div>

                <div className="quick-metrics-section">
                  <h4 className="quick-metrics-section-title">This Week:</h4>
                  <ul className="quick-metrics-list">
                    <li>• {quickMetrics?.this_week.logins || 0} logins</li>
                    <li>• {quickMetrics?.this_week.transactions || 0} transactions</li>
                    <li>• {quickMetrics?.this_week.new_customers || 0} new customers</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Activity Feed & Integration Status */}
      <ActivityFeed />
      <IntegrationStatus />
    </div>
  );
};

